/**
 * Variant names for ResponsiveImage on listing cards.
 *
 * Listing cards are configured for `listing-card` / `listing-card-2x` (custom
 * marketplace image variants). The discovery merged-search endpoint and TN RAM
 * catalog often return only default Sharetribe variants (`scaled-small`,
 * `scaled-medium`). Filtering strictly by `listing-card` yields an empty
 * variants array → ResponsiveImage shows "no image" even when valid URLs exist.
 *
 * Visiting a listing detail page loads full variants into Redux, which is why
 * thumbnails can appear after navigating back.
 */

import { optimizeImageUrl } from './imageOptimizer';

const DEFAULT_FALLBACK_VARIANTS = ['scaled-medium', 'scaled-small', 'scaled-large'];

const hasUsableUrl = (variants, key) => {
  const url = variants?.[key]?.url;
  return typeof url === 'string' && url.trim() !== '';
};

/**
 * @param {object|null|undefined} image denormalised image entity
 * @param {string} [variantPrefix] e.g. `listing-card` from layout config
 * @returns {string[]} variant keys suitable for ResponsiveImage `variants` prop
 */
export const listingCardResponsiveVariants = (image, variantPrefix = 'listing-card') => {
  const variants = image?.attributes?.variants;
  if (!variants || typeof variants !== 'object') {
    return [];
  }

  const keys = Object.keys(variants);
  const prefixed = keys.filter(k => k.startsWith(variantPrefix)).filter(k => hasUsableUrl(variants, k));
  if (prefixed.length > 0) {
    return prefixed;
  }

  const fallbacks = DEFAULT_FALLBACK_VARIANTS.filter(k => hasUsableUrl(variants, k));
  if (fallbacks.length > 0) {
    return fallbacks;
  }

  // Last resort: any variant key with a URL (unusual marketplace configs).
  return keys.filter(k => hasUsableUrl(variants, k));
};

/**
 * Primary `src` + `srcSet` for listing-card image (blur background + sharp foreground).
 */
export const listingCardImageSources = (image, variantPrefix = 'listing-card') => {
  const variantKeys = listingCardResponsiveVariants(image, variantPrefix);
  if (!variantKeys.length) {
    return null;
  }

  const variants = image.attributes.variants;
  // For primary-event listings every "variant" points at the same raw S3 original
  // (see buildPrimaryEventImage), so a naive srcSet is meaningless. Routing each width
  // through the optimizer (no-op for already-optimized Sharetribe imgix URLs) turns this
  // into a real responsive srcSet. Disabled → URLs pass through unchanged.
  const srcSet = variantKeys
    .map(key => {
      const variant = variants[key];
      if (!variant?.url) {
        return null;
      }
      const width = variant.width != null ? variant.width : 400;
      return `${optimizeImageUrl(variant.url, width)} ${width}w`;
    })
    .filter(Boolean)
    .join(', ');

  let bestUrl = null;
  let bestWidth = -1;
  for (const key of variantKeys) {
    const variant = variants[key];
    if (!variant?.url) {
      continue;
    }
    const width = Number(variant.width);
    if (!bestUrl || (Number.isFinite(width) && width > bestWidth)) {
      bestWidth = Number.isFinite(width) ? width : bestWidth;
      bestUrl = variant.url;
    }
  }

  if (!bestUrl) {
    return null;
  }

  return {
    src: optimizeImageUrl(bestUrl, Number.isFinite(bestWidth) && bestWidth > 0 ? bestWidth : undefined),
    srcSet: srcSet || undefined,
  };
};

/**
 * First denormalised image on a listing, if any.
 */
export const listingFirstImage = listing => {
  if (!listing) {
    return null;
  }
  const images = listing.images;
  if (Array.isArray(images) && images.length > 0) {
    return images[0];
  }
  return null;
};
